// QualitySelector — generation quality slider (gpt-image-2: medium|high).
// Only renders when the selected model exposes config.quality_levels.
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { StudioModel } from './ModelSelector';

interface Props {
  model: StudioModel | null;
  value: string;
  onChange: (q: string) => void;
}

export function QualitySelector({ model, value, onChange }: Props) {
  const { t } = useTranslation();
  const levels = model?.config.quality_levels ?? [];
  const fallback = model?.config.default_quality ?? 'medium';

  // Reset to the model default whenever the model changes / value invalid.
  useEffect(() => {
    if (levels.length > 0 && !levels.includes(value)) onChange(fallback);
  }, [model?.code, levels, value, fallback, onChange]);

  if (levels.length <= 1) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {t('generation.quality.label')}
      </label>
      <div className="flex gap-2">
        {levels.map(q => (
          <button
            key={q}
            type="button"
            onClick={() => onChange(q)}
            className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
              value === q
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-background hover:border-primary/50'
            }`}
          >
            <span className="font-medium">{t(`generation.quality.${q}`, q)}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t('generation.quality.hint')}</p>
    </div>
  );
}
